import React, { useMemo } from "react";
import { QueryKey, useInfiniteQuery } from "react-query";
import { useHistory, useRouteMatch } from "react-router-dom";
import { CellClickedEvent } from "@ag-grid-community/core";

import { Company, PermissionLevel } from "fe-shared/src/types/domain";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import { hasPermission } from "fe-shared/src/helpers/permissions";
import useStorage from "fe-shared/src/hooks/useStorage";

import InvoicesDataGrid from "components/InvoicesDataGrid";
import { getInvoicesRequestParams } from "fe-shared-transactions/src/helpers/invoicesRequestConverter";

import { INVOICES_QUERY_KEY, PAGE_SIZE, PERMISSIONS } from "consts";

import { GridInvoiceList, GridInvoice } from "fe-shared-transactions/src/types";

import * as api from "./modules/api";

interface Props {
  company: Company;
  lastOpenedInvoiceNumber: string | null;
  clearLastOpenedInvoiceNumber: () => void;
}

const Invoices = ({
  company,
  lastOpenedInvoiceNumber,
  clearLastOpenedInvoiceNumber,
}: Props) => {
  const history = useHistory();
  const match = useRouteMatch();
  const [options, setOptions] = useStorage<DataGridOptions>(
    "webAppInvoicesOptions",
    {
      sortDirection: "desc",
      sortField: "submittedDate",
    },
    true
  );
  const [filters, setFilters] = useStorage<DataGridFilters>(
    "webAppInvoicesFilters",
    {},
    true
  );

  const canViewList = hasPermission(company?.permissions, {
    name: PERMISSIONS.invoicesList,
    level: PermissionLevel.Read,
  });

  const canExport = hasPermission(company?.permissions, {
    name: PERMISSIONS.invoicesListExport,
    level: PermissionLevel.Full,
  });

  const canViewDetails = hasPermission(company?.permissions, {
    name: PERMISSIONS.invoiceDetails,
    level: PermissionLevel.Read,
  });

  const requestParams = useMemo(
    () => getInvoicesRequestParams(filters, options),
    [filters, options]
  );

  const invoicesQueryKey: QueryKey = [INVOICES_QUERY_KEY, requestParams];

  const invoices = useInfiniteQuery<GridInvoiceList<GridInvoice>, unknown>(
    invoicesQueryKey,
    api.getInvoices,
    {
      enabled: canViewList,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getFetchMore: (lastGroup, allGroups) =>
        allGroups.length * PAGE_SIZE < lastGroup.total
          ? allGroups.length + 1
          : undefined,
    }
  );

  const onCellClicked = (e: CellClickedEvent) => {
    if (canViewDetails) {
      history.push(`${match.url}/${e.data.invoiceNumber}`);
    }
  };

  return canViewList ? (
    <InvoicesDataGrid
      invoices={invoices}
      companyId={company?.companyId}
      lastOpenedInvoiceNumber={lastOpenedInvoiceNumber}
      clearLastOpenedInvoiceNumber={clearLastOpenedInvoiceNumber}
      options={options}
      filters={filters}
      setOptions={setOptions}
      setFilters={setFilters}
      canExport={canExport}
      invoicesQueryKey={invoicesQueryKey}
      onCellClicked={onCellClicked}
    />
  ) : null;
};

export default Invoices;
